import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { AudienceCard, ButtonLink, Card, Container, Screenshot, SectionLabel, StagePill } from '../../design';
import { pagePricing, priced, pricingText } from '../../lib/pricing';
import { AnalyticsEvent, ctaEvent } from '../../utils/analytics';
import { CtaBand } from './CtaBand';
import { PortContainer, ShopFront, Spot } from './illustrations';
import { MKT } from './links';
import { MarketingLayout } from './MarketingLayout';
import { PLANS } from './PricingPage.data';
import { ProductHero } from './ProductParts';
import { CROPS, SHOTS_NOTE } from './shots';
import { TrustGrid } from './TrustGrid';
import './product-shared.css';
import './dealers.css';
// /for-dealers: what a dealership gets. Every claim is checked against the app (Help → Hunts,
// Customs, Costs, Your shop & team; the dealership setup guide).
const JOBS = [
    {
        k: '相場',
        title: 'Bid ranges you can check',
        text: 'Conservative, Target and Aggressive bids, worked out from sold lots of the same grade and generation, with a link to each lot. The Max bid is the most you can pay and still make your target profit once every landed cost is in.'
    },
    {
        k: '売値',
        title: 'Price it to sell',
        text: 'Every estimate has sale prices beside it. Your own past sales come first, then US auction results and dealer listings from a web search, each linked, with the gap between them.'
    },
    {
        k: '在庫',
        title: 'Stock and margin',
        text: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                "Each car sits on one line from ",
                /*#__PURE__*/ _jsx(StagePill, {
                    stage: "watching"
                }),
                " to ",
                /*#__PURE__*/ _jsx(StagePill, {
                    stage: "sold"
                }),
                ". Its landed cost is built from your paperwork, and its profit shows up as soon as you record the sale."
            ]
        })
    },
    {
        k: '通関',
        title: 'Customs per shipment',
        text: 'The seller’s documents open a customs entry, with your cars matched by chassis number. Your broker gets the ISF form filled in and every email drafted. Their fee goes on each car as an estimate until the invoice replaces it.'
    },
    {
        k: '人員',
        title: 'The whole team',
        text: 'Your plan sets the seats. Invite people by email as an owner, admin or member, and everyone works from the same shop: its cars, hunts, customers, mailbox and exporter logins.'
    },
    {
        k: '拡張',
        title: 'Searches from your own browser',
        text: 'The Chrome extension runs hunts in your shop’s Chrome with your own exporter sessions, so sites that block automated sign-ins see you. You can use our in-app browser instead.'
    }
];
// The dealership setup guide's steps (models.WelcomeSteps, WelcomeDealership), as titled in it.
const SETUP = [
    'Your shop',
    'Bring in your team',
    'Connect your mailbox',
    'Auction access',
    'Your auction logins',
    'What it costs to land a car',
    'Your customs broker',
    'Your first hunt',
    'How a run works',
    'What the app emails, and when'
];
export default function ForDealersPage() {
    const t = pricingText(pagePricing());
    return /*#__PURE__*/ _jsx(MarketingLayout, {
        testid: Testids.app.marketing.dealersRoot,
        children: /*#__PURE__*/ _jsxs("main", {
            children: [
                /*#__PURE__*/ _jsx(ProductHero, {
                    k: "商",
                    label: "For dealerships",
                    title: "Source for every customer and close the auction tabs.",
                    watermark: "商",
                    lead: "Right Hand DMS runs the import side of a JDM dealership. Each customer gets a standing hunt through your own exporter accounts. Every auction sheet comes in English, bid ranges come from sold lots, and customs is handled per shipment, with what each car really cost at the end. Your whole team shares it.",
                    actions: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(ButtonLink, {
                                href: MKT.signupFor('dealer'),
                                "data-testid": Testids.app.marketing.dealersCta,
                                ...ctaEvent(AnalyticsEvent.ctaSignup, 'dealers-hero', 'dealer'),
                                children: "Get started"
                            }),
                            /*#__PURE__*/ _jsx(ButtonLink, {
                                href: MKT.bookCall,
                                variant: "secondary",
                                ...ctaEvent(AnalyticsEvent.ctaBookCall, 'dealers-hero', 'dealer'),
                                children: "Book a 20-minute call"
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(Screenshot, {
                        ...CROPS.search,
                        bare: true,
                        invert: true,
                        eager: true,
                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("b", {
                                    children: "A customer’s hunt."
                                }),
                                " ",
                                SHOTS_NOTE
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Spot, {
                    width: 320,
                    children: /*#__PURE__*/ _jsx(ShopFront, {})
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "mk-dealers__orders-section",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "mk-dealers__orders-div"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mk-dealers__orders-div-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-dealers__orders-div-3",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-dealers__orders-div-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-dealers__orders-span",
                                                    children: "注文"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-dealers__orders-span-2",
                                                    children: "Sourcing for customers"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "mk-dealers__orders-h2",
                                            children: "A hunt for every customer. Updates they don’t have to chase."
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-dealers__orders-div-5",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-dealers__orders-div-6",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-dealers__orders-span-3",
                                                            children: "01"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "mk-dealers__orders-div-7",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("b", {
                                                                    className: "mk-dealers__orders-b",
                                                                    children: "One hunt per customer."
                                                                }),
                                                                " Start it from their criteria, or straight from the email thread where they asked."
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-dealers__orders-div-6",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-dealers__orders-span-3",
                                                            children: "02"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "mk-dealers__orders-div-7",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("b", {
                                                                    className: "mk-dealers__orders-b",
                                                                    children: "Alerts and digests drafted for you."
                                                                }),
                                                                ' ',
                                                                "Match alerts, weekly digests and purchase summaries wait in your Outbox and go from your own mailbox once you approve them. Your bids never go in them."
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-dealers__orders-div-6",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-dealers__orders-span-3",
                                                            children: "03"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "mk-dealers__orders-div-7",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("b", {
                                                                    className: "mk-dealers__orders-b",
                                                                    children: "Wins land in inventory."
                                                                }),
                                                                " The car you won for them is already a car record, tied to the customer and the paperwork. Their hunt closes on its own."
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("a", {
                                            href: MKT.product('hunts'),
                                            className: "mk-dealers__orders-a",
                                            children: "How hunts work →"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-dealers__orders-div-8",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-dealers__orders-div-9",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-dealers__orders-span-4",
                                                    children: "Draft · to R. Delgado"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-dealers__orders-span-5",
                                                    children: "Example · waiting for your OK"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-dealers__orders-div-10",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-dealers__orders-div-11",
                                                    children: [
                                                        "From: you@yourshop.com",
                                                        /*#__PURE__*/ _jsx("br", {}),
                                                        "Subject: 3 Land Cruisers this week"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "mk-dealers__orders-p",
                                                    children: "Hi Ray, three came up this week that fit what you asked for. I’d go for the first one. Diesel, clean interior, a little underbody rust we can treat."
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-dealers__orders-div-12",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "mk-dealers__orders-div-13",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: "1995 Land Cruiser VX · 4 · 118k km"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: "JCD"
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "mk-dealers__orders-div-13",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: "1997 Land Cruiser VX · 3.5 · 142k km"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: "JDM Auc"
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "mk-dealers__orders-div-14",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: "1996 Land Cruiser GX · 4 · 131k km"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: "PCJ"
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-dealers__orders-div-15",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-dealers__orders-span-6",
                                                            children: "Send"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-dealers__orders-span-7",
                                                            children: "Edit"
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-dealers__jobs",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        children: [
                            /*#__PURE__*/ _jsx(SectionLabel, {
                                k: "仕事",
                                className: "mk-dealers__label",
                                children: "The rest of the job"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-dealers__h2",
                                children: "Buy right, land it, and know what you made."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mk-dealers__grid",
                                children: JOBS.map((j)=>/*#__PURE__*/ _jsxs(Card, {
                                        className: "mk-dealers__job",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "mk-dealers__job-k",
                                                "aria-hidden": "true",
                                                children: j.k
                                            }),
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "mk-dealers__job-title",
                                                children: j.title
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mk-dealers__job-text",
                                                children: j.text
                                            })
                                        ]
                                    }, j.k))
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spot, {
                    width: 300,
                    children: /*#__PURE__*/ _jsx(PortContainer, {})
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-dealers__shots mk-ground--sora",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        children: [
                            /*#__PURE__*/ _jsx(SectionLabel, {
                                k: "画面",
                                className: "mk-dealers__label",
                                children: "The real app"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-dealers__h2",
                                children: "Every car’s cost, and the day’s decisions."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-dealers__shots-grid",
                                children: [
                                    /*#__PURE__*/ _jsx(Screenshot, {
                                        ...CROPS.costs,
                                        bare: true,
                                        invert: true,
                                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "Landed cost, line by line."
                                                }),
                                                " Each line is read from an invoice, a 7501 or a receipt (or typed in), then added up per car."
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Screenshot, {
                                        ...CROPS.today,
                                        bare: true,
                                        invert: true,
                                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "Today."
                                                }),
                                                " Probable wins to confirm, sheets that never came and hunts that found nothing. Each one has the button that fixes it."
                                            ]
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-dealers__note",
                                children: SHOTS_NOTE
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-dealers__setup mk-ground--kinari",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        className: "mk-dealers__setup-inner",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-dealers__setup-copy",
                                children: [
                                    /*#__PURE__*/ _jsx(SectionLabel, {
                                        k: "始",
                                        className: "mk-dealers__label",
                                        children: "Getting set up"
                                    }),
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "mk-dealers__h2",
                                        children: "A setup guide on your first sign-in."
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mk-dealers__text",
                                        children: "The shop’s owner gets a guided setup the first time they sign in. Every step has Continue and Do this later, plus Skip where the step isn’t essential, and your progress saves as you go. The Shop plan comes with a setup call. On Dealer, we set up your exporters, mailbox and current stock with you."
                                    }),
                                    /*#__PURE__*/ _jsx("ol", {
                                        className: "mk-dealers__setup-list",
                                        children: SETUP.map((s, i)=>/*#__PURE__*/ _jsxs("li", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dealers__setup-n",
                                                        children: String(i + 1).padStart(2, '0')
                                                    }),
                                                    s
                                                ]
                                            }, s))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-dealers__plans",
                                children: [
                                    PLANS.map((p)=>/*#__PURE__*/ _jsxs("a", {
                                            href: MKT.pricingFor('dealer'),
                                            className: "mk-dealers__plan",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-dealers__plan-k",
                                                    "aria-hidden": "true",
                                                    children: p.k
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-dealers__plan-name",
                                                    children: p.name
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "mk-dealers__plan-price",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced(`plan.${p.id}.price`),
                                                            children: t[`plan.${p.id}.price`]
                                                        }),
                                                        " / month"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-dealers__plan-users",
                                                    ...priced(`plan.${p.id}.users`),
                                                    children: t[`plan.${p.id}.users`]
                                                })
                                            ]
                                        }, p.id)),
                                    /*#__PURE__*/ _jsx("a", {
                                        href: MKT.pricingFor('dealer'),
                                        className: "mk-dealers__plans-more",
                                        children: "Compare the plans →"
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(TrustGrid, {
                    title: "Your shop, your accounts, your say."
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-dealers__other",
                    children: /*#__PURE__*/ _jsx(Container, {
                        children: /*#__PURE__*/ _jsx(AudienceCard, {
                            k: "個",
                            who: "Not a dealership?",
                            title: "Importing just one, for yourself?",
                            promise: "Import my own walks one person through bringing in a JDM car or truck. The sign-up covers your first car and includes 90 days of searching.",
                            action: /*#__PURE__*/ _jsx(ButtonLink, {
                                href: MKT.importMyOwn,
                                variant: "secondary",
                                ...ctaEvent(AnalyticsEvent.ctaAudience, 'dealers-other', 'individual'),
                                children: "See Import my own"
                            }),
                            aside: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        ...priced('pass.price'),
                                        children: t['pass.price']
                                    }),
                                    ", first car included"
                                ]
                            })
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(CtaBand, {
                    audience: "dealer"
                })
            ]
        })
    });
}
